import type React from 'react';
import { Children } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@squareup/shared-ui-react';
import { ProductPageHeader, type ProductPageHeaderProps } from '../product-page-header/ProductPageHeader';
import { ProductPageHeaderSecondary } from '../index';
import errorTrackingClient from '../../../utils/error-tracking-client';
import styles from './PageLayout.module.css';

export enum PageLayoutVariant {
  LandingPage = 'landingPage',
  OneColumn = 'oneColumn',
  TwoColumn = 'twoColumn',
}

const CLASS_MAP: Record<PageLayoutVariant, { container: string; content: string; footerClass: string }> = {
  [PageLayoutVariant.LandingPage]: {
    container: styles.layoutLandingPage,
    content: '',
    footerClass: styles.layoutLandingPageFooter,
  },
  [PageLayoutVariant.OneColumn]: {
    container: '',
    content: styles.layoutOneColumn,
    footerClass: styles.layoutOneColumnFooter,
  },
  [PageLayoutVariant.TwoColumn]: {
    container: styles.layoutTwoColumnContainer,
    content: styles.layoutTwoColumn,
    footerClass: styles.layoutTwoColumnFooter,
  },
};

interface PageLayoutProps extends Omit<ProductPageHeaderProps, 'title' | 'subtitle'> {
  readonly variant: PageLayoutVariant;
  // BANKWEB-1305: Remove after Loans pages are migrated to ProductPageHeader
  readonly header?: React.ReactNode;
  readonly showPageError?: boolean;
  readonly heading: string;
  readonly headingSubtitle?: string;
  readonly children: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly testId?: string;
}

export const PageLayout = ({
  variant,
  header,
  showPageError = false,
  goBack,
  heading,
  headingSubtitle,
  locationSelector,
  units,
  currentUnitToken,
  handleSelectLocation,
  buttons,
  children,
  footer,
  balance,
  balanceSuperText,
  testId = 'page-layout',
}: PageLayoutProps) => {
  const childCount = Children.count(children);

  if (variant === PageLayoutVariant.OneColumn) {
    if (childCount !== 1) {
      errorTrackingClient.captureException(`PageLayout OneColumn variant expects 1 child, received ${childCount}.`);
    }
  }

  if (variant === PageLayoutVariant.TwoColumn) {
    if (childCount !== 2) {
      errorTrackingClient.captureException(`PageLayout TwoColumn variant expects 2 children, received ${childCount}.`);
    }
  }

  const { container, content, footerClass } = CLASS_MAP[variant];
  const { t } = useTranslation();

  return (
    <div className={styles.layout} data-testid={testId}>
      <div className={container}>
        {header ?? (
          variant === PageLayoutVariant.OneColumn
            ? (
              <ProductPageHeaderSecondary
                title={heading}
                subtitle={headingSubtitle}
                goBack={goBack}
              />
            )
            : (
              <ProductPageHeader
                title={heading}
                subtitle={headingSubtitle}
                goBack={goBack}
                locationSelector={locationSelector}
                units={units}
                currentUnitToken={currentUnitToken}
                handleSelectLocation={handleSelectLocation}
                buttons={buttons}
                balance={balance}
                balanceSuperText={balanceSuperText}
              />
            ))}
        <main className={content}>
          {showPageError
            ? (
              <div>
                <div className={styles.layoutError} data-testid={`${testId}-error`}>
                  <Icon
                    aria-hidden
                    iconName="exclamation-circle"
                    size={48}
                    fill="var(--core-fill-30-light-mode-color)"
                  />
                  <div className={`heading-20 ${styles.errorTitle}`}>
                    {t('bankingShared.error.message')}
                  </div>
                  <div className={`paragraph-30 ${styles.errorSubtext}`}>
                    {t('bankingShared.error.subtitle')}
                  </div>
                </div>
              </div>
            )
            : children}
        </main>
        {footer && (
          <footer className={footerClass}>{footer}</footer>
        )}
      </div>
    </div>
  );
};
